/**
 * Which workspace an agency is looking at — the whole portfolio, or one
 * creator — read from the address and nowhere else, so a refresh, a shared
 * link and Back all land where the reader was. `?profile=<id>` was already the
 * dashboard's way of naming a profile; the workspace is that same parameter.
 */

export const WORKSPACE_PARAM = 'profile';
export const AGENCY_OVERVIEW_PATH = '/portfolio';
export const CREATOR_OVERVIEW_PATH = '/dashboard';
/** Agency Setup is the dashboard with no creator chosen. */
export const AGENCY_SETUP_PATH = CREATOR_OVERVIEW_PATH;

/** One row of the switcher: never the whole profile, which carries her legal name and addresses. */
export interface CreatorOption {
  id: string;
  stageName: string;
  /** The handle that matched the search, else her first; null when she has none. */
  username: string | null;
}

export type Workspace = { kind: 'agency' } | { kind: 'creator'; profileId: string };

/** Screens whose data the API can narrow to one creator. */
const CREATOR_SECTIONS = ['/dashboard', '/leaks', '/cases', '/reports', '/whitelist'];

/** Screens that make sense with no creator chosen: the account's own, or every creator's at once. */
const AGENCY_SECTIONS = [
  '/portfolio',
  '/leaks',
  '/cases',
  '/reports',
  '/whitelist',
  '/profiles',
  '/team',
  '/settings',
  '/plan',
  '/billing',
  '/notifications',
  '/help',
  '/referrals',
  '/submit',
];

/** `/profiles/sample/edit` is the demo profile, not a creator anybody can switch to. */
const EDIT = /^\/profiles\/([^/]+)\/edit\/?$/;

function sectionOf(pathname: string): string {
  return `/${pathname.split('/')[1] ?? ''}`;
}

function editedProfile(pathname: string): string | null {
  const id = EDIT.exec(pathname)?.[1] ?? null;
  return id && id !== 'sample' ? id : null;
}

function paramsOf(search: URLSearchParams | string): URLSearchParams {
  return typeof search === 'string' ? new URLSearchParams(search) : search;
}

export function workspaceOf(pathname: string, search: URLSearchParams | string): Workspace {
  const edited = editedProfile(pathname);
  if (edited) return { kind: 'creator', profileId: edited };
  const wanted = paramsOf(search).get(WORKSPACE_PARAM);
  if (wanted && CREATOR_SECTIONS.includes(sectionOf(pathname))) return { kind: 'creator', profileId: wanted };
  return { kind: 'agency' };
}

/** Whether a screen exists in a creator's workspace, so its rail link should carry her. */
export function isCreatorSection(pathname: string): boolean {
  return editedProfile(pathname) !== null || CREATOR_SECTIONS.includes(sectionOf(pathname));
}

/** `href` inside the given workspace: a creator screen carries her id, anything else is left alone. */
export function inWorkspace(href: string, workspace: Workspace): string {
  if (workspace.kind === 'agency') return href;
  const [path, query = ''] = href.split('?');
  if (!CREATOR_SECTIONS.includes(sectionOf(path))) return href;
  const params = new URLSearchParams(query);
  params.set(WORKSPACE_PARAM, workspace.profileId);
  return `${path}?${params.toString()}`;
}

/**
 * Where switching to `target` goes from here: the same screen when the target
 * workspace has it, that workspace's overview when it does not. A detail page
 * (one case, one profile's form) belongs to whoever it was opened for, so it
 * gives way to its list rather than showing another creator's case.
 */
export function workspaceHref(pathname: string, search: URLSearchParams | string, target: Workspace): string {
  const section = sectionOf(pathname);
  const params = new URLSearchParams(paramsOf(search));
  if (target.kind === 'agency') {
    if (editedProfile(pathname) || !AGENCY_SECTIONS.includes(section)) return AGENCY_OVERVIEW_PATH;
    if (section === CREATOR_OVERVIEW_PATH) return AGENCY_OVERVIEW_PATH;
    params.delete(WORKSPACE_PARAM);
    const rest = params.toString();
    return rest ? `${pathname}?${rest}` : pathname;
  }
  const id = encodeURIComponent(target.profileId);
  if (editedProfile(pathname) || section === '/profiles') return `/profiles/${id}/edit`;
  const landing = CREATOR_SECTIONS.includes(section) ? section : CREATOR_OVERVIEW_PATH;
  return `${landing}?${WORKSPACE_PARAM}=${id}`;
}
